import React, { Fragment } from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import {
  PageHeader,
  ListingTable,
  SidebarHeading,
  FootNote,
  FootNoteLink,
  TaxFilter,
  SimpleDataTable,
  DangerousWrapper,
  Section,
  makeBreadcrumb
} from '@budget-monorepo/shared-ui';
import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';

const TaxItem = (props) => {
  const { location, data } = props;
  const {
    content, site, filter, footnotes
  } = data;
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = content.parent;
  const { pageTitle, breadcrumbprops } = makeBreadcrumb({
    href: '/tax-expenditure-budget',
    text: 'Tax Expenditure Budget',
    sections: [content.data.taxType, content.data.taxExpenditure, content.data.taxItemNumber]
  });
  const layoutProps = {
    activeIndex: 0,
    pre: () => <TaxFilter {...{ location, filter }} />,
    above: () => <PageHeader category={content.data.taxItemNumber} title={content.data.taxItemName} />,
    containerClasses: 'main-content main-content--two',
    pageTitle,
    breadcrumbprops,
    dataTimeStamp: modifiedTime,
    buildTimeStamp: site.buildTime
  };
  const item = {
    headers: content.headers,
    data: [content.data]
  };
  // metadata for the page
  const metaTitle = `${content.data.taxItemNumber}: ${content.data.taxItemName}`;
  const metaDescription = `Tax expenditure data for ${content.title} under ${content.data.taxType} - ${content.data.taxExpenditure}.`;
  return(
    <Fragment>
      <HelmetData {...{
        metaTitle,
        metaDescription,
        metaType: 'dataset',
        mg_mod_date,
        mg_mod_datetime
      }}
      />
      <Layout {...layoutProps}>
        <div className="page-content">
          <Section>
            <p>
              <DangerousWrapper htmlTag="span" rawHtml={content.data.description} />
              { /* If the tax item has a footnote, render the link to the
                   footnote at the end of the description. */
                content.data.footnote && content.data.footnote.map((note) => <FootNoteLink index={note.index} />)
              }
            </p>
            <DangerousWrapper rawHtml={`<strong>Origin:</strong> ${content.data.origin}`} htmlTag="p" />
            <SimpleDataTable title={`${content.data.taxItemNumber} ${content.data.taxItemName}`} table={item} maxColWidth={100} mantissa={1} download={location.pathname} />
          </Section>
          <Section>
            { /* If the tax item has footnotes, render the footnote text
                 at the bottom of the page beneath the table. */
              content.data.footnote && footnotes && content.data.footnote.map((note) => (
                <FootNote key={`footnote.${note.index}`} index={note.index}>
                  {footnotes.footnotes[parseInt(note.index, 10) - 1] && footnotes.footnotes[parseInt(note.index, 10) - 1].text}
                </FootNote>
              ))}
          </Section>
        </div>
        {content.key && (
          <aside className="sidebar">
            <SidebarHeading title={content.key.title} />
            <ListingTable rows={content.key.tableData.rows} />
          </aside>
        )}
      </Layout>
    </Fragment>
  );
};

TaxItem.propTypes = {
  data: PropTypes.object,
  location: PropTypes.object,
  pageContext: PropTypes.object
};

export default TaxItem;

export const query = graphql`
  query($slug: String!) {
    site {
      ...SiteMetadata
    }
    content: taxItem(fields: { slug: { eq: $slug } }) {
      headers {
        name
        type
        align
        value
      }
      data {
        taxType
        taxExpenditure
        taxItemNumber
        taxItemName
        description
        origin
        fyFourYearsAgo
        fyThreeYearsAgo
        fyTwoYearsAgo
        fyOneYearsAgo
        fyThisYear
        footnote {
          field
          index
        }
      }
      key {
        title
        type
        tableData {
          rows {
            label
            items
          }
        }
      }
      # Get the last modified timestamp for the file powering the page
      ...TaxItemDataTimeStamp
    }
    filter: taxItemJson {
      data {
        taxType
        taxExpenditure
        taxItemNumber
        taxItemName
      }
    }
    # Get all of the footnotes for the tax items.
    footnotes: taxItemJson {
      footnotes {
        index
        text
      }
    }
  }
`;
